import { useState } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { ArrowLeft, EnvelopeSimple, Phone, MapPin, PaperPlaneTilt } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      toast.error('Por favor preencha todos os campos obrigatórios.');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('Mensagem enviada com sucesso! Entraremos em contacto brevemente.');
        setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erro ao enviar mensagem.');
      }
    } catch (error) {
      toast.error('Erro de conexão. Tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: EnvelopeSimple,
      label: 'Email',
      value: 'suporte@passeioperfeito.pt',
      href: 'mailto:suporte@passeioperfeito.pt'
    },
    {
      icon: Phone,
      label: 'Telefone',
      value: '928 314 198',
      href: 'tel:+351928314198'
    },
    {
      icon: MapPin,
      label: 'Localização',
      value: 'Lisboa, Portugal',
      href: null
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="container mx-auto px-4 py-8 max-w-4xl">
        <Link to="/">
          <Button variant="ghost" className="mb-6" data-testid="contact-back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="text-3xl font-bold font-outfit mb-4">Contactos</h1>
          <p className="text-lg text-muted-foreground mb-10">
            Tem alguma dúvida ou sugestao? Estamos ca para ajudar.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
            {contactInfo.map((info) => {
              const Icon = info.icon;
              return (
                <Card key={info.label}>
                  <CardContent className="p-6 text-center">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
                      <Icon weight="duotone" className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="font-semibold text-sm mb-1">{info.label}</h3>
                    {info.href ? (
                      <a 
                        href={info.href} 
                        className="text-sm text-muted-foreground hover:text-primary transition-colors"
                        data-testid={`contact-${info.label.toLowerCase()}`}
                      >
                        {info.value}
                      </a>
                    ) : (
                      <p className="text-sm text-muted-foreground" data-testid={`contact-${info.label.toLowerCase()}`}>
                        {info.value}
                      </p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Contact Form */}
          <Card>
            <CardContent className="p-6 md:p-8">
              <h2 className="text-xl font-semibold mb-6">Envie-nos uma mensagem</h2>
              
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Nome *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="O seu nome"
                      required
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="email@exemplo.com"
                      required
                      data-testid="contact-email-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="phone">Telefone (opcional)</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="912 345 678"
                      data-testid="contact-phone-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="subject">Assunto *</Label>
                    <Input
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      placeholder="Como podemos ajudar?"
                      required
                      data-testid="contact-subject-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="message">Mensagem *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Escreva a sua mensagem aqui..."
                    rows={5}
                    required
                    data-testid="contact-message-input"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full md:w-auto rounded-full"
                  disabled={submitting}
                  data-testid="contact-submit-btn"
                >
                  {submitting ? (
                    'A enviar...'
                  ) : (
                    <>
                      <PaperPlaneTilt className="w-4 h-4 mr-2" />
                      Enviar Mensagem
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </motion.div>
      </main>

      <Footer />
    </div>
  );
}
